import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOIS, fmtMontant, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, ConfirmDialog, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { NoteDebitCombobox } from "@/components/NoteDebitCombobox";
import { Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileCheck2, Loader2, Printer } from "lucide-react";

const EMPTY = { note_debit_id: "", montant_apurement: "", date: todayISO(), mois: MOIS[new Date().getMonth()] };

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value || "—"}</span>
    </div>
  );
}

export default function NotesApurement() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/notes-apurement", { params: { page, page_size: PAGE_SIZE, q: filter.trim() || undefined } });
    setRows(data.items);
    setMeta({ total: data.total, pages: data.pages });
    setLoading(false);
  }, [page, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const selected = selectedNote;

  const openNew = () => { setEditing(null); setForm(EMPTY); setSelectedNote(null); setSheet(true); };
  const openEdit = async (n) => {
    setEditing(n);
    setForm({ note_debit_id: n.note_debit_id, montant_apurement: n.montant_apurement, date: (n.date || "").slice(0, 10), mois: n.mois });
    setSelectedNote({ numero: n.numero_debit, redevable: n.redevable, code: n.code, note_taxation: n.note_taxation,
      acte_generateur_nom: n.acte_generateur_nom, province: n.province, ville: n.ville, commune: n.commune,
      quartier: n.quartier, annee: n.annee_debit, mois: n.mois_debit, montant: n.montant_debit, monnaie: n.monnaie });
    setSheet(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.note_debit_id) { toast.error("Sélectionnez une note de perception"); return; }
    if (!form.montant_apurement || Number(form.montant_apurement) <= 0) { toast.error("Montant invalide"); return; }
    const payload = { ...form, montant_apurement: Number(form.montant_apurement) };
    try {
      if (editing) await api.put(`/notes-apurement/${editing.id}`, payload);
      else await api.post("/notes-apurement", payload);
      toast.success(editing ? "Apurement modifié" : "Note d'apurement créée");
      setSheet(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/notes-apurement/${confirm.id}`);
      toast.success("Note supprimée");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirm(null);
    }
  };

  return (
    <div data-testid="notes-apurement-page">
      <PageHeader title="Notes d'Apurement" subtitle="Enregistrement des paiements et apurements">
        {canWrite && (
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="add-note-apurement-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Nouvel apurement
          </Button>
        )}
      </PageHeader>

      <div className="mb-4">
        <Input value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          placeholder="Filtrer par redevable, code, n° apurement ou n° débit…" className="max-w-md" data-testid="filter-apurement" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={FileCheck2} title="Aucune note d'apurement" hint={filter ? "Aucun résultat pour ce filtre." : "Enregistrez un apurement à partir d'une note de perception."} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>N° Apurement</TableHead>
                  <TableHead>N° Perception</TableHead>
                  <TableHead>Redevable</TableHead>
                  <TableHead>Acte</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Montant Perception</TableHead>
                  <TableHead className="text-right">Montant Apuré</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((n) => (
                  <TableRow key={n.id} className="hover:bg-slate-50" data-testid="note-apurement-row">
                    <TableCell className="font-mono text-xs font-semibold text-slate-700">{n.numero}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{n.numero_debit}</TableCell>
                    <TableCell className="font-medium text-slate-800">{n.redevable}</TableCell>
                    <TableCell className="text-slate-600">{n.acte_generateur_nom}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{fmtDate(n.date)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-700">{fmtMontant(n.montant_debit, n.monnaie)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-700">{fmtMontant(n.montant_apurement, n.monnaie)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => window.open(`/print/note-apurement/${n.id}`, "_self")} data-testid="print-note-apurement-btn">
                        <Printer className="h-4 w-4 text-slate-600" />
                      </Button>
                      {canWrite && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(n)} data-testid="edit-note-apurement-btn">
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setConfirm(n)} data-testid="delete-note-apurement-btn">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {!loading && <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={PAGE_SIZE} onPage={setPage} />}
      </Card>

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="note-apurement-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">{editing ? "Modifier l'apurement" : "Nouvelle note d'apurement"}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-1.5">
              <Label>Note de Perception (Redevable) *</Label>
              <NoteDebitCombobox
                value={form.note_debit_id}
                disabled={!!editing}
                selectedLabel={selected ? `${selected.numero} — ${selected.redevable}` : ""}
                onSelect={(n) => { setField("note_debit_id", n.id); setSelectedNote(n); }}
              />
            </div>

            {selected && (
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="note-debit-info">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Informations de la note de perception</p>
                <InfoRow label="Code" value={selected.code} />
                <InfoRow label="Note de Taxation" value={selected.note_taxation} />
                <InfoRow label="Acte Générateur" value={selected.acte_generateur_nom} />
                <InfoRow label="Province / Ville" value={`${selected.province} / ${selected.ville}`} />
                <InfoRow label="Commune / Quartier" value={`${selected.commune} / ${selected.quartier || "—"}`} />
                <InfoRow label="Année / Mois" value={`${selected.annee} / ${selected.mois}`} />
                <InfoRow label="Montant dû" value={fmtMontant(selected.montant, selected.monnaie)} />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-1">
                <Label>Montant Apurement *</Label>
                <Input type="number" step="0.01" value={form.montant_apurement}
                  onChange={(e) => setField("montant_apurement", e.target.value)} className="font-mono" data-testid="field-montant-apurement" />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="field-date-apurement" />
              </div>
              <div className="space-y-1.5">
                <Label>Mois</Label>
                <Select value={form.mois} onValueChange={(v) => setField("mois", v)}>
                  <SelectTrigger data-testid="field-mois-apurement"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOIS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setSheet(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-note-apurement-btn">Enregistrer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Supprimer la note d'apurement ?"
        description={`La note ${confirm?.numero} sera définitivement supprimée.`}
        onConfirm={remove}
      />
    </div>
  );
}
